import styled from 'styled-components';

import { Card } from '@components/card';
import { useDebouncedValue } from '@components/component-helpers';
import { HtmlContent } from '@components/html-content';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useMinimumDelay } from '@hooks/use-minimum-delay';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useEmailDomains, useEmailSettings } from 'api/company/hooks';
import { useActionEmailTemplate } from 'api/journeys/hooks';

import {
  buildEmailPreviewProps,
  buildEmailPreviewPropsFromTravellerAction,
  getPreviewAttachments,
  PreviewEmailProps,
} from './email-preview';

import { AttachmentList } from '../common';
import {
  brokenChipStyles,
  renderWithVariableChips,
  variableChipStyles,
} from '../shared/variable-chips';

// Re-export pure helpers so existing call sites can keep importing from
// './email-preview-modal'. Tests should import from './email-preview'
// directly to avoid pulling React/browser-only modules in node test envs.
export { buildEmailPreviewProps, buildEmailPreviewPropsFromTravellerAction, getPreviewAttachments };
export type { PreviewEmailProps };

export const usePreviewEmailModal = () => useModal(PreviewEmail, { variant: 'LARGE' });

const EMPTY_EMAIL = '<p></p>';

type EmailItemProps = {
  infoText: string;
  text: string;
  isPlaceholder?: boolean;
  brokenLabels?: ReadonlySet<string>;
};

// Split out of the `PreviewEmail` modal so `EmailPreviewPanel` can render it in
// the sidebar's own React tree: `showModal`/`useModal` snapshot props at open
// time, so a modal can never re-render as the user keeps editing.
export const EmailPreviewContent = (props: PreviewEmailProps) => {
  const {
    to,
    subject,
    shortId,
    action,
    attachments,
    cc,
    bcc,
    replyTo,
    sendFromEmailId,
    liveContent,
    brokenLabels,
  } = props;
  const brokenLabelSet = brokenLabels?.length ? new Set(brokenLabels) : undefined;
  const { t } = useScopedTranslate(locale);
  // The live content is part of the react-query key (see useActionEmailTemplate) --
  // without debouncing, fast-changing drafts (typed labels, streamed popover drafts)
  // would fire one preview POST per keystroke.
  const debouncedLiveContent = useDebouncedValue(liveContent, 400);
  // `keepPreviousData` holds the last render while the next is in flight, so the
  // spinner is only for the first render.
  const { data, isLoading, isError } = useActionEmailTemplate(
    action,
    shortId,
    debouncedLiveContent
  );
  // Floor the spinner's visible time so a fast response doesn't flash it.
  // `?? isLoading` covers the hook's initial null tick so it shows immediately.
  const isPreviewLoading = useMinimumDelay(isLoading, 500) ?? isLoading;
  const renderedEmail = data ?? EMPTY_EMAIL;
  // On failure, don't leave the last good HTML up -- it reads as a current preview.
  const hasRenderedEmail = !isError && (Boolean(data) || !isPreviewLoading);

  const formatRecipientList = (
    value?: string | string[],
    emptyFallback: string | null = null
  ): string | null => {
    return Array.isArray(value) && value.length > 0 ? value.join(', ') : emptyFallback;
  };

  const hasRecipient = Array.isArray(to) && to.length > 0;
  const recipient = formatRecipientList(to, t('noRecipient')) as string;
  const ccInfo = formatRecipientList(cc);
  const bccInfo = formatRecipientList(bcc);
  const replyToInfo = formatRecipientList(replyTo);
  const hasSubject = Boolean(subject);
  const subjectInfo = subject ? subject : t('noSubject');
  const emailSettingsQuery = useEmailSettings();
  const emailSettings = emailSettingsQuery.data;
  const domainsQuery = useEmailDomains();
  const emailAttachments = attachments ?? [];

  const resolveFromAddress = (): { name: string; email: string } => {
    if (!sendFromEmailId || sendFromEmailId === 'system') {
      return { name: '50skills', email: 'noreply@mg.50skills.app' };
    }

    if (domainsQuery.data) {
      for (const domain of domainsQuery.data) {
        const address = domain.emailAddresses.find((addr) => addr.shortId === sendFromEmailId);
        if (address) {
          return { name: address.fromName, email: address.fromAddress };
        }
      }
    }

    return {
      name: emailSettings?.fromName ?? '50skills',
      email: emailSettings?.fromAddress ?? 'noreply@mg.50skills.app',
    };
  };

  const fromAddress = resolveFromAddress();

  return (
    <Stack space={8}>
      <Stack space={2}>
        <Stack space={1}>
          <EmailItem
            infoText={t('from')}
            text={t('email', {
              name: fromAddress.name,
              email: fromAddress.email,
            })}
          />
          <Separator />
        </Stack>
        <Stack space={1}>
          <EmailItem
            infoText={t('to')}
            text={recipient}
            isPlaceholder={!hasRecipient}
            brokenLabels={brokenLabelSet}
          />
          <Separator />
        </Stack>
        {ccInfo && (
          <Stack space={1}>
            <EmailItem infoText={t('cc')} text={ccInfo} brokenLabels={brokenLabelSet} />
            <Separator />
          </Stack>
        )}
        {bccInfo && (
          <Stack space={1}>
            <EmailItem infoText={t('bcc')} text={bccInfo} brokenLabels={brokenLabelSet} />
            <Separator />
          </Stack>
        )}
        {replyToInfo && (
          <Stack space={1}>
            <EmailItem infoText={t('replyTo')} text={replyToInfo} brokenLabels={brokenLabelSet} />
            <Separator />
          </Stack>
        )}
        <Stack space={1}>
          <EmailItem
            infoText={t('subject')}
            text={subjectInfo}
            isPlaceholder={!hasSubject}
            brokenLabels={brokenLabelSet}
          />
          {emailAttachments.length > 0 && <Separator />}
        </Stack>

        {emailAttachments.length > 0 && (
          <Stack space={4} align="top" orientation="horizontal">
            <StyledText color="textSecondary" font="semibold14" text={t('attachments')} />
            <Stack space={1}>
              <AttachmentList attachments={emailAttachments} />
            </Stack>
          </Stack>
        )}
      </Stack>
      <EmailBody space={1}>
        <ThemedEmailContent>
          {/* Only the backend render is server-sanitized, so it's the only
              value we inject as HTML -- never the raw author template. */}
          {hasRenderedEmail && <HtmlContent font="regular14" html={renderedEmail} />}
          {!hasRenderedEmail && isError && (
            <PreviewMessageContainer>
              <Text color="textSecondary" font="regular14" text={t('previewFailed')} />
            </PreviewMessageContainer>
          )}
          {!hasRenderedEmail && !isError && (
            <PreviewMessageContainer>
              <SizedRelativeSpinner size={32} />
            </PreviewMessageContainer>
          )}
        </ThemedEmailContent>
      </EmailBody>
    </Stack>
  );
};

export const PreviewEmail = (props: PreviewEmailProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Modal>
      <Modal.SimpleHeader title={t('emailPreview')} />
      <ContentContainer>
        <Modal.Content>
          <EmailPreviewContent {...props} />
        </Modal.Content>
      </ContentContainer>
    </Modal>
  );
};

const EmailItem = (props: EmailItemProps) => {
  const { text, infoText, isPlaceholder, brokenLabels } = props;
  return (
    <Stack space={4} align="center" orientation="horizontal">
      <StyledText color="textSecondary" font="semibold14" text={infoText} />
      <Text
        font="regular14"
        color={isPlaceholder ? 'textHelper' : undefined}
        text={renderWithVariableChips(text, brokenLabels)}
      />
    </Stack>
  );
};

const ContentContainer = styled.div({
  overflowY: 'auto',
});

// The design shows the rendered email on a plain white sheet; Card's default
// surface is the grey fill, which read as a band around the email.
// (backgroundLightDark is white — backgroundPrimary is the page grey.)
const EmailBody = styled(Card)({
  backgroundColor: theme.color.backgroundLightDark,
});

const PreviewMessageContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  padding: pxToRem(32),
});

const ThemedEmailContent = styled.div({
  '&, & *:not(a):not(code)': {
    color: `${theme.color.textPrimary} !important`,
  },
  // Plain links take the theme's link blue. A link carrying its own inline
  // background-color is a rendered CTA button from the backend (see
  // actions/base/link_rendering.py) — it already ships a matching inline text
  // colour, and forcing blue here painted the label blue-on-blue, i.e. invisible.
  '& a:not([style*="background-color"]), & a:not([style*="background-color"]) *': {
    color: `${theme.color.elementBlue100} !important`,
  },
  '& code': {
    ...variableChipStyles,
    color: `${theme.color.elementBlue100} !important`,
  },
  // The backend wraps a broken variable (deleted source) in <ins>; render that
  // chip in red so it reads as broken rather than a normal mapped variable.
  '& ins': {
    textDecoration: 'none',
  },
  '& ins > code, & ins > code *': brokenChipStyles,
});

const StyledText = styled(Text)({
  width: pxToRem(120),
});

const locale = {
  en: {
    emailPreview: 'Email preview',
    from: 'FROM',
    to: 'TO',
    cc: 'CC',
    bcc: 'BCC',
    replyTo: 'REPLY-TO',
    subject: 'SUBJECT',
    poweredBy: 'Powered by 50skills',
    email: '{name} <{email}>',
    noContent: 'Email has no content',
    previewFailed: "Couldn't load the preview. Check your connection and try again.",
    noSubject: 'Email has no subject',
    noRecipient: 'Not set',
    attachments: 'ATTACHMENTS',
  },
};
